import type { WorkspaceMemberWithUserDto } from '@up2stay/up2promo-api-client';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  cn,
} from '@up2stay/up2promo-ui';
import { useId, useMemo, useState } from 'react';

import {
  useResolvedWorkspaceMember,
  useWorkspaceMemberSearch,
} from '@/core/hooks/use-workspace-member-picker';
import { triggerSelectionHaptic } from '@/core/lib/dnd-haptics';
import {
  workspaceMemberAvatarUrl,
  workspaceMemberDisplayName,
  workspaceMemberInitials,
  workspaceMemberSecondaryLine,
} from '@/core/lib/workspace-member-display';
import { useLocale } from '@/core/providers/dictionary-provider';

export type ProductProfileUserComboboxProps = {
  workspaceId: string | undefined;
  value: string | null;
  onChange: (userId: string | null) => void;
  label: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  loadErrorMessage: string;
  noneLabel?: string;
  allowNone?: boolean;
  required?: boolean;
  disabled?: boolean;
};

type ProfileUserPickerItem =
  | { kind: 'none'; userId: null; label: string }
  | {
      kind: 'member';
      userId: string;
      label: string;
      member: WorkspaceMemberWithUserDto;
    };

function MemberAvatar({
  member,
  className,
}: {
  member: WorkspaceMemberWithUserDto;
  className?: string;
}) {
  const src = workspaceMemberAvatarUrl(member);
  const initials = workspaceMemberInitials(member);
  return (
    <Avatar className={cn('size-8 shrink-0', className)}>
      {src ? <AvatarImage src={src} alt="" /> : null}
      <AvatarFallback className="text-[10px] font-medium">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

function MemberRowContent({ member }: { member: WorkspaceMemberWithUserDto }) {
  const primary = workspaceMemberDisplayName(member);
  const secondary = workspaceMemberSecondaryLine(member);
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <MemberAvatar member={member} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{primary}</span>
        {secondary ? (
          <span className="block truncate text-xs text-muted-foreground">
            {secondary}
          </span>
        ) : null}
      </span>
    </div>
  );
}

export function ProductProfileUserCombobox({
  workspaceId,
  value,
  onChange,
  label,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  loadErrorMessage,
  noneLabel,
  allowNone = false,
  required,
  disabled,
}: ProductProfileUserComboboxProps) {
  // Names the trigger after the visible label (DEV-540).
  const labelId = useId();
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const {
    data: searchResults = [],
    isFetching,
    isError,
  } = useWorkspaceMemberSearch(workspaceId, { search, enabled: open });

  const { data: resolvedMember } = useResolvedWorkspaceMember(
    workspaceId,
    value,
  );

  const selectedFromSearch = useMemo(
    () => searchResults.find((m) => m.userId === value),
    [searchResults, value],
  );

  const selectedMember = selectedFromSearch ?? resolvedMember ?? null;

  const items = useMemo<ProfileUserPickerItem[]>(() => {
    const collator = locale === 'de' ? 'de' : 'en';
    const members = [...searchResults]
      .sort((a, b) =>
        workspaceMemberDisplayName(a).localeCompare(
          workspaceMemberDisplayName(b),
          collator,
          { sensitivity: 'base' },
        ),
      )
      .flatMap((member) => {
        const uid = member.userId?.trim();
        if (!uid) return [];
        return [
          {
            kind: 'member' as const,
            userId: uid,
            label: workspaceMemberDisplayName(member),
            member,
          },
        ];
      });

    if (allowNone && noneLabel) {
      return [{ kind: 'none', userId: null, label: noneLabel }, ...members];
    }
    return members;
  }, [searchResults, locale, allowNone, noneLabel]);

  const selected = useMemo<ProfileUserPickerItem | null>(() => {
    if (!value) {
      return allowNone && noneLabel
        ? { kind: 'none', userId: null, label: noneLabel }
        : null;
    }
    const fromItems = items.find(
      (item) => item.kind === 'member' && item.userId === value,
    );
    if (fromItems) return fromItems;
    if (selectedMember) {
      return {
        kind: 'member',
        userId: value,
        label: workspaceMemberDisplayName(selectedMember),
        member: selectedMember,
      };
    }
    return {
      kind: 'member',
      userId: value,
      label: value,
      member: {
        userId: value,
      } as WorkspaceMemberWithUserDto,
    };
  }, [value, items, selectedMember, allowNone, noneLabel]);

  const displayLabel = selectedMember
    ? workspaceMemberDisplayName(selectedMember)
    : value?.trim()
      ? value
      : placeholder;

  return (
    <div className="space-y-2">
      <label id={labelId} className="text-sm font-medium leading-none">
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </label>
      <Combobox<ProfileUserPickerItem>
        items={items}
        value={selected}
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setSearch('');
        }}
        onValueChange={(next) => {
          if (!next) return;
          void triggerSelectionHaptic();
          onChange(next.userId);
          setSearch('');
        }}
        onInputValueChange={(next, { reason }) => {
          if (reason === 'item-press') return;
          setSearch(next);
        }}
        filter={null}
        itemToStringLabel={(item) => item.label}
        isItemEqualToValue={(a, b) => a.userId === b.userId}
        disabled={disabled || !workspaceId}
        inputValue={search}
      >
        <ComboboxTrigger
          aria-labelledby={labelId}
          render={
            <Button
              type="button"
              variant="outline"
              size="lg"
              className={cn(
                'w-full justify-between gap-2 px-3 font-normal',
                !selectedMember && !value && 'text-muted-foreground',
              )}
            />
          }
        >
          <span className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
            {selectedMember ? (
              <MemberAvatar member={selectedMember} className="size-5" />
            ) : null}
            <span className="min-w-0 flex-1 truncate text-left text-sm">
              {displayLabel}
            </span>
          </span>
        </ComboboxTrigger>
        <ComboboxContent
          className="max-w-[min(100vw-2rem,400px)]"
          align="start"
        >
          <ComboboxInput placeholder={searchPlaceholder} showTrigger={false} />
          {isError ? (
            <div className="py-6 text-center text-sm text-destructive">
              {loadErrorMessage}
            </div>
          ) : null}
          {!isError && isFetching && items.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              …
            </div>
          ) : null}
          <ComboboxEmpty>
            {!isError && !isFetching ? emptyMessage : null}
          </ComboboxEmpty>
          <ComboboxList>
            {(item: ProfileUserPickerItem) => (
              <ComboboxItem
                key={item.userId ?? '__none__'}
                value={item}
                className="gap-2"
              >
                {item.kind === 'none' ? (
                  <span className="truncate">{item.label}</span>
                ) : (
                  <MemberRowContent member={item.member} />
                )}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  );
}
